import { Injectable, inject } from '@angular/core';
import { AlertController } from '@ionic/angular/standalone';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, catchError, filter, map, switchMap } from 'rxjs';
import { AppNotification } from 'src/app/models/app-notification.model';
import { RoughagePlusEvent } from 'src/app/models/events.model';
import { AppInsightsService } from 'src/app/services/app-insights.service';
import { NotificationService } from 'src/app/services/notification.service';

import { NotificationActions } from './actions';

@Injectable()
export class NotificationEffects {
  private readonly actions$ = inject(Actions);
  private readonly alertController = inject(AlertController);
  private readonly notificationService = inject(NotificationService);
  private readonly appInsightsService = inject(AppInsightsService);

  loadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.loadNotifications),
      switchMap(() =>
        this.notificationService.getNotifications().pipe(
          map((notifications) =>
            NotificationActions.notificationsLoaded({
              notifications,
            }),
          ),
          catchError(() => EMPTY), // It is not need to show a toast message as the popups are not critical.
        ),
      ),
    ),
  );

  notificationsLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.notificationsLoaded),
      map((action) => action.notifications.find((x) => x.showPopup)),
      filter((notification): notification is AppNotification => !!notification),
      map((notification) =>
        NotificationActions.showNotificationPopup({
          notification,
        }),
      ),
    ),
  );

  showNotificationPopup$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationActions.showNotificationPopup),
        map(({ notification }) => {
          this.alertController
            .create({
              cssClass: 'notification',
              header: notification.title,
              message: notification.content,
              buttons: [
                ...(notification.link && notification.buttonText
                  ? [
                      {
                        text: notification.buttonText,
                        handler: () =>
                          this.notificationCTAButtonHandler(notification),
                        cssClass: 'call-to-action',
                      },
                    ]
                  : []),
                {
                  text: notification.dismissText,
                  cssClass: 'dismiss',
                },
              ],
            })
            .then((alert) => alert.present());
        }),
      ),
    { dispatch: false },
  );

  notificationCTAButtonHandler(notification: AppNotification) {
    this.appInsightsService.trackEvent(
      RoughagePlusEvent.NotificationCTAClicked,
      {
        value: notification.code,
      },
    );
    window.open(notification.link, '_blank');
  }
}
